import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';

/**
 * DriverAssignmentsDrawer — admin slide-in panel showing a single
 * driver's transport assignments for a chosen date.
 *
 * Why this exists:
 *  Drivers are first-class team members but their day-to-day work
 *  (routes, kids, custody) lives in the Transport module. This drawer
 *  brings that view directly into the Staff Profiles list so admins
 *  can answer "who is Driver X picking up today?" without leaving the
 *  Staff Ops tab.
 *
 * Props:
 *   open       : boolean
 *   driverId   : string  — user_id of the driver (manifests are keyed by user, not staff_profile)
 *   driverName : string  — heading
 *   onClose    : () => void
 */
const ymd = (d) => {
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const STATUS_TONES = {
  pending: { bg: 'hsl(45 95% 92%)', fg: 'hsl(35 80% 30%)', label: 'Pending' },
  in_progress: { bg: 'hsl(220 80% 92%)', fg: 'hsl(220 70% 30%)', label: 'In progress' },
  completed: { bg: 'hsl(140 50% 92%)', fg: 'hsl(140 50% 30%)', label: 'Completed' },
  cancelled: { bg: 'hsl(0 70% 94%)', fg: 'hsl(0 60% 35%)', label: 'Cancelled' },
};

const CHILD_STATUS_TONES = {
  scheduled: { bg: 'hsl(var(--muted))', fg: 'hsl(var(--muted-foreground))', label: '⏳ Scheduled' },
  picked_up: { bg: 'hsl(220 80% 92%)', fg: 'hsl(220 70% 30%)', label: '🚐 Picked up' },
  dropped_off: { bg: 'hsl(140 50% 92%)', fg: 'hsl(140 50% 30%)', label: '🏠 Dropped off' },
  no_show: { bg: 'hsl(0 70% 94%)', fg: 'hsl(0 60% 35%)', label: '❌ No-show' },
};

export function DriverAssignmentsDrawer({ open, driverId, driverName, onClose }) {
  const [date, setDate] = useState(() => ymd(new Date()));
  const [manifests, setManifests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !driverId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getDriverManifests({ driver_id: driverId, date });
        if (!cancelled) setManifests(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, driverId, date]);

  if (!open) return null;

  const totalChildren = manifests.reduce((acc, m) => acc + (m.children?.length || 0), 0);

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{ alignItems: 'stretch', justifyContent: 'flex-end' }}
      data-testid="driver-assignments-drawer"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(560px, 100%)',
          height: '100%',
          background: 'hsl(var(--background))',
          borderLeft: '1px solid hsl(var(--border))',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid hsl(var(--border))', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
          <div>
            <h2 style={{ margin: 0 }}>🚐 Transport — {driverName || 'Driver'}</h2>
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>
              Routes & assigned children for the selected date.
            </p>
          </div>
          <button type="button" className="btn btn-ghost" onClick={onClose} data-testid="driver-drawer-close" aria-label="Close">×</button>
        </div>

        <div style={{ padding: '0.85rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem', borderBottom: '1px solid hsl(var(--border))', flexWrap: 'wrap' }}>
          <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="driver-drawer-date"
            style={{ fontSize: '0.85rem' }}
          />
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setDate(ymd(new Date()))}
            data-testid="driver-drawer-today"
            style={{ fontSize: '0.78rem', padding: '0.2rem 0.55rem' }}
          >
            Today
          </button>
          {!loading && !error && (
            <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))' }}>
              {manifests.length} route(s) · {totalChildren} child(ren)
            </span>
          )}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.25rem' }}>
          {error && <div className="error-message" data-testid="driver-drawer-error">{error}</div>}
          {loading && <p>Loading…</p>}

          {!loading && !error && manifests.length === 0 && (
            <div className="empty-state" data-testid="driver-drawer-empty">
              <p style={{ margin: 0 }}>No transport routes for this driver on {date}.</p>
              <small style={{ color: 'hsl(var(--muted-foreground))' }}>
                Tip: routes are generated automatically from confirmed bookings. Visit the Transport tab to generate or edit them.
              </small>
            </div>
          )}

          {!loading && !error && manifests.map((m) => {
            const stTone = STATUS_TONES[m.status] || STATUS_TONES.pending;
            return (
              <div key={m._id} className="card" data-testid={`driver-manifest-${m._id}`} style={{ padding: '0.85rem', marginBottom: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 4 }}>
                  <div>
                    <strong>{m.route_name || 'Route'}</strong>
                    <div style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))' }}>
                      {m.route_type ? `${m.route_type} · ` : ''}
                      {m.vehicle_name || 'Vehicle TBD'}
                      {m.departure_time ? ` · 🕒 ${m.departure_time}` : ''}
                    </div>
                  </div>
                  <span
                    className="badge"
                    style={{
                      background: stTone.bg, color: stTone.fg,
                      fontSize: '0.7rem', padding: '0.15rem 0.45rem',
                      borderRadius: '999px', fontWeight: 600,
                    }}
                  >
                    {stTone.label}
                  </span>
                </div>

                {Array.isArray(m.children) && m.children.length > 0 ? (
                  <div style={{ marginTop: '0.6rem', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                    {m.children.map((c, i) => {
                      const childTone = CHILD_STATUS_TONES[c.status] || CHILD_STATUS_TONES.scheduled;
                      return (
                        <div
                          key={c.child_id || c.id || i}
                          data-testid={`driver-manifest-child-${m._id}-${c.child_id || i}`}
                          style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '0.35rem 0.5rem',
                            background: 'hsl(var(--muted) / 0.4)',
                            borderRadius: 6,
                            fontSize: '0.85rem',
                            gap: 6,
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <strong>{c.child_name || c.full_name || 'Child'}</strong>
                            {c.pickup_address || c.dropoff_address ? (
                              <div style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))' }}>
                                {c.pickup_address ? `📍 ${c.pickup_address}` : ''}
                                {c.dropoff_address ? ` → ${c.dropoff_address}` : ''}
                              </div>
                            ) : null}
                          </div>
                          <span
                            className="badge"
                            style={{
                              background: childTone.bg, color: childTone.fg,
                              fontSize: '0.65rem', padding: '0.1rem 0.4rem',
                              borderRadius: '999px', fontWeight: 600,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {childTone.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))' }}>
                    No children currently on this manifest.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default DriverAssignmentsDrawer;
